import React, { useState, useRef, useEffect, useCallback, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SaferSavvyLogo from '../components/SaferSavvyLogo';
import { updateSEO, buildBreadcrumbSchema } from '../utils/seo';

const OTP_LENGTH   = 6;
const RESEND_TIMER = 30;
const MAX_ATTEMPTS = 5;

/* ─── Google G icon ─── */
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" className="flex-shrink-0">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.33 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.67 14.62 48 24 48z"/>
  </svg>
);

const EyeOpen = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const Login: React.FC = () => {
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepSigned, setKeepSigned]     = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [showOtp, setShowOtp]           = useState(false);
  const [otpDigits, setOtpDigits]       = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [userId, setUserId]             = useState('');
  const [otpEmail, setOtpEmail]         = useState('');
  const [isLoading, setIsLoading]       = useState(false);
  const [attempts, setAttempts]         = useState(0);
  const [timer, setTimer]               = useState(RESEND_TIMER);
  const [canResend, setCanResend]       = useState(false);
  const navigate = useNavigate();

  /* ── SEO: Login page meta ── */
  useEffect(() => {
    updateSEO({
      title: "Sign In to SaferSavvy — University Transport Automation Platform",
      description:
        "Log in to your SaferSavvy account to access AI-powered university transport automation features including real-time bus tracking, smart routing, and automated attendance.",
      canonical: "/login",
      keywords: "SaferSavvy login, university transport login, student transport app login",
      noIndex: true,
      jsonLd: buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Login", url: "/login" },
      ]),
    });
  }, []);

  const otpRefs  = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = useCallback(() => {
    setTimer(RESEND_TIMER); setCanResend(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current!); setCanResend(true); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  /* ── Auto-redirect if already logged in ── */
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isExpired = payload.exp && payload.exp * 1000 < Date.now();

      if (isExpired) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('roleData');
        return;
      }

      const raw = localStorage.getItem('user');
      const role = raw ? JSON.parse(raw).role : null;
      if (role) navigate(redirectByRole(role), { replace: true });
    } catch {
      /* malformed token — stay on login */
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectByRole = (role: string) => {
    switch (role) {
      case 'student':        return '/student-dashboard';
      case 'driver':         return '/driver-dashboard';
      case 'transportAdmin': return '/admin-dashboard';
      default:               return '/';
    }
  };

  const storeAuthData = (data: any) => {
    if (data.accessToken)  localStorage.setItem('accessToken',  data.accessToken);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    if (data.user)         localStorage.setItem('user',         JSON.stringify(data.user));
    if (data.data)         localStorage.setItem('roleData',     JSON.stringify(data.data));
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields'); return; }
    setIsLoading(true); setError('');
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}api/auth/login`, { email, password });
      if (res.status === 200) {
        const { skipOtp, userRole } = res.data;
        if (skipOtp) {
          setSuccess('Login successful! Redirecting...');
          storeAuthData(res.data);
          setTimeout(() => navigate(redirectByRole(userRole)), 1200);
        } else {
          setUserId(res.data.userId); setOtpEmail(res.data.email || email);
          setSuccess('OTP sent to your email. Please check your inbox.');
          startTimer();
          setTimeout(() => { setShowOtp(true); setSuccess(''); setTimeout(() => otpRefs.current[0]?.focus(), 100); }, 1200);
        }
      }
    } catch (err: any) {
      if (err.response) {
        const m: Record<number,string> = { 400:'Invalid request.', 401:'Invalid email or password.', 403:'Account not verified yet.', 429:'Too many attempts.', 500:'Server error.' };
        setError(m[err.response.status] || err.response.data?.message || 'Login failed.');
      } else if (err.request) setError('No response from server.');
      else setError('An unexpected error occurred.');
    } finally { setIsLoading(false); }
  };

  const submitOtp = async (code: string) => {
    if (code.length !== OTP_LENGTH) { setError('Please enter the complete OTP'); return; }
    setIsLoading(true); setError('');
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}api/auth/verify-otp`, { userId, otp: code });
      if (res.status === 200) {
        setSuccess('Login successful! Redirecting...');
        storeAuthData(res.data);
        setTimeout(() => navigate(redirectByRole(res.data.user?.role || 'transportAdmin')), 1200);
      }
    } catch (err: any) {
      if (err.response) {
        if (err.response.status === 401) {
          const curr = err.response.data?.attempts ?? attempts + 1; setAttempts(curr);
          setError(curr >= MAX_ATTEMPTS ? 'Too many attempts. Try again after 30 min.' : `Incorrect OTP. ${MAX_ATTEMPTS - curr} attempts left.`);
        } else setError(err.response.data?.message || 'OTP verification failed.');
      } else setError('No response from server.');
    } finally { setIsLoading(false); }
  };

  const handleOtpSubmit  = (e: FormEvent) => { e.preventDefault(); submitOtp(otpDigits.join('')); };
  const handleOtpChange  = (i: number, v: string) => {
    if (!/^\d*$/.test(v)) return;
    const d = [...otpDigits]; d[i] = v.slice(-1); setOtpDigits(d);
    if (v && i < OTP_LENGTH - 1) otpRefs.current[i + 1]?.focus();
    if (v && i === OTP_LENGTH - 1 && d.join('').length === OTP_LENGTH) submitOtp(d.join(''));
  };
  const handleOtpKeyDown = (i: number, e: React.KeyboardEvent) => { if (e.key === 'Backspace' && !otpDigits[i] && i > 0) otpRefs.current[i - 1]?.focus(); };
  const handleOtpPaste   = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!p) return;
    const d = Array(OTP_LENGTH).fill(''); p.split('').forEach((c, i) => { d[i] = c; });
    setOtpDigits(d); otpRefs.current[Math.min(p.length, OTP_LENGTH - 1)]?.focus();
    if (p.length === OTP_LENGTH) submitOtp(p);
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    setIsLoading(true); setError('');
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}api/auth/resend-otp`, { userId });
      if (res.status === 200) { setOtpDigits(Array(OTP_LENGTH).fill('')); setSuccess('New OTP sent'); startTimer(); setTimeout(() => { setSuccess(''); otpRefs.current[0]?.focus(); }, 1500); }
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to resend OTP.'); }
    finally { setIsLoading(false); }
  };

  const maskedEmail = otpEmail.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(b.length) + c);

  return (
    <div className="h-screen flex overflow-hidden">

      {/* ── Left panel — dark branding ── */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] bg-[#1D3047] flex-col px-10 xl:px-14 py-8 overflow-hidden" style={{ backgroundImage: 'radial-gradient(ellipse at 20% 60%, rgba(45,95,158,0.45) 0%, transparent 70%)' }}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <SaferSavvyLogo size={80} />
          {/* <span className="text-white font-bold text-xl tracking-tight">
            Safer<span style={{ color: '#E3B02E' }}>Savvy</span>
          </span> */}
        </Link>

        {/* Headline */}
        <div className="flex-1 flex flex-col justify-center mb-5 min-h-0">
          <h1 className="text-4xl xl:text-5xl font-black text-white leading-tight mb-3">
            Your campus<br />
            transport, <span style={{ color: '#E3B02E' }}>reimagined.</span>
          </h1>
          <p className="text-blue-300 text-sm font-medium tracking-wide">
            Smarter routes. Safer rides. Every day.
          </p>
        </div>

        {/* Image — constrained so it never causes overflow */}
        <img src="/login-busimg.png" alt="Bus" className="w-full mb-6 rounded-2xl object-contain flex-shrink-0" style={{ height: '250px' }} />
      </div>

      {/* ── Right panel — form ── */}
      <div className="flex-1 bg-white flex flex-col overflow-y-auto">
        {/* Top bar */}
        <div className="flex justify-end px-8 py-5">
          <span className="text-sm text-gray-500">
            New here?{' '}
            <Link to="/signup" className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors">
              Create an account
            </Link>
          </span>
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-8 sm:px-12 lg:px-16 xl:px-24">
          <div className="w-full max-w-md">

            <AnimatePresence mode="wait">
              {!showOtp ? (
                <motion.div key="login" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
                  <h2 className="text-3xl font-black text-[#1D3047] mb-1">Welcome back</h2>
                  <p className="text-gray-500 text-sm mb-7">Sign in to track your bus, manage complaints, and stay connected with your campus transport.</p>

                  {/* Alerts */}
                  {error && (
                    <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-2.5 mb-5">
                      <span>{error}</span>
                      <button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600 font-bold text-lg leading-none">×</button>
                    </div>
                  )}
                  {success && (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg px-4 py-2.5 mb-5">
                      <span>{success}</span>
                    </div>
                  )}

                  {/* Google btn */}
                  <button type="button" className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-xl py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-5">
                    <GoogleIcon />
                    Continue with Google
                  </button>

                  {/* Divider */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 font-medium tracking-widest">OR WITH EMAIL</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <input
                        type="email" placeholder="you@university.edu" value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D5F9E]/30 focus:border-[#2D5F9E] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password}
                          onChange={e => setPassword(e.target.value)}
                          className="w-full px-4 py-2.5 pr-11 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D5F9E]/30 focus:border-[#2D5F9E] transition-all"
                        />
                        <button type="button" tabIndex={-1} onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff /> : <EyeOpen />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={keepSigned} onChange={e => setKeepSigned(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-[#1D3047] accent-[#1D3047]" />
                        <span className="text-sm text-gray-600">Keep me signed in</span>
                      </label>
                      <Link to="/forgot-password" className="text-sm font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors">
                        Forgot password?
                      </Link>
                    </div>

                    <button type="submit" disabled={isLoading}
                      className="w-full bg-[#1D3047] hover:bg-[#2D5F9E] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm mt-1">
                      {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>
                  </form>

                  <p className="text-center text-sm text-gray-500 mt-6">
                    Don't have an account?{' '}
                    <Link to="/signup" className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors">Sign up</Link>
                  </p>
                </motion.div>
              ) : (
                /* ── OTP screen ── */
                <motion.div key="otp" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>

                  {/* Step indicator */}
                  <div className="flex items-center gap-2 mb-8">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#1D3047]/10 flex items-center justify-center">
                        <svg className="w-3.5 h-3.5 text-[#1D3047]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                        </svg>
                      </div>
                      <span className="text-xs text-gray-400 font-medium">Sign in</span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-[#1D3047]/20 via-[#E3B02E] to-[#E3B02E]/30" />
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#E3B02E] flex items-center justify-center shadow-sm">
                        <span className="text-white text-xs font-black">2</span>
                      </div>
                      <span className="text-xs text-[#1D3047] font-semibold">Verify</span>
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="relative w-16 h-16 mx-auto mb-5">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg shadow-[#1D3047]/20"
                      style={{ background: 'linear-gradient(135deg, #1D3047 0%, #2D5F9E 100%)' }}>
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#E3B02E] rounded-full border-2 border-white flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18 8h-1V6A5 5 0 007 6v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2zm-6 9a2 2 0 110-4 2 2 0 010 4zm3.1-9H8.9V6a3.1 3.1 0 016.2 0v2z"/>
                      </svg>
                    </div>
                  </div>

                  <h2 className="text-2xl font-black text-[#1D3047] text-center mb-1.5">Verify your identity</h2>
                  <p className="text-gray-500 text-sm text-center mb-4">
                    Enter the 6-digit code sent to
                  </p>

                  {/* Email badge */}
                  <div className="flex justify-center mb-6">
                    <span className="inline-flex items-center gap-2 bg-[#1D3047]/6 border border-[#1D3047]/12 rounded-full px-4 py-1.5 text-sm font-semibold text-[#1D3047]">
                      <svg className="w-3.5 h-3.5 text-[#2D5F9E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"/>
                      </svg>
                      {maskedEmail}
                    </span>
                  </div>

                  {/* Alerts */}
                  {error && (
                    <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-2.5 mb-4">
                      <span>{error}</span>
                      <button onClick={() => setError('')} className="ml-3 font-bold text-lg leading-none opacity-60 hover:opacity-100">×</button>
                    </div>
                  )}
                  {success && (
                    <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-xl px-4 py-2.5 mb-4 text-center">{success}</div>
                  )}

                  <form onSubmit={handleOtpSubmit}>
                    {/* OTP boxes */}
                    <div className="flex justify-center gap-2.5 mb-5">
                      {otpDigits.map((d, i) => (
                        <input key={i}
                          ref={el => { otpRefs.current[i] = el; }}
                          type="text" inputMode="numeric" maxLength={1} value={d}
                          onChange={e => handleOtpChange(i, e.target.value)}
                          onKeyDown={e => handleOtpKeyDown(i, e)}
                          onPaste={i === 0 ? handleOtpPaste : undefined}
                          className={`w-11 h-14 text-center text-2xl font-black rounded-xl border-2 focus:outline-none transition-all duration-150
                            ${d
                              ? 'border-[#E3B02E] bg-[#E3B02E]/8 text-[#1D3047]'
                              : 'border-gray-200 bg-gray-50 text-gray-800'
                            } focus:border-[#1D3047] focus:bg-white focus:shadow-sm`}
                        />
                      ))}
                    </div>

                    {/* Timer / Resend */}
                    <div className="flex items-center justify-center mb-5">
                      {!canResend ? (
                        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-1.5">
                          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2"/>
                          </svg>
                          <span className="text-xs text-gray-500">Resend in <span className="font-bold text-[#1D3047]">{timer}s</span></span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          Didn't receive it?{' '}
                          <button type="button" onClick={handleResendOtp} disabled={isLoading}
                            className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors disabled:opacity-50">
                            {isLoading ? 'Sending…' : 'Resend code'}
                          </button>
                        </div>
                      )}
                    </div>

                    <button type="submit" disabled={isLoading || otpDigits.join('').length < OTP_LENGTH}
                      className="w-full bg-[#1D3047] hover:bg-[#2D5F9E] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                      {isLoading ? 'Verifying…' : 'Verify & continue'}
                    </button>
                  </form>

                  <button type="button" disabled={isLoading}
                    onClick={() => { setShowOtp(false); setOtpDigits(Array(OTP_LENGTH).fill('')); setError(''); clearInterval(timerRef.current!); }}
                    className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-400 hover:text-[#1D3047] font-medium mt-5 transition-colors disabled:opacity-40">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                    </svg>
                    Back to sign in
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;
